import { useState, useMemo, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useModelStore } from '../../../state/store/chat/use-model-store';
// import { useNavigate } from 'react-router-dom';
import { useGetLlmModel } from '../../../hooks/use-llm-model';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks/use-mobile';
import { useSubscriptionStore } from 'state/store/subscription/useSubscriptionStore';
import { PLANS } from 'constant/plan';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { MessageRole } from 'features/chat/types/chat';
import { useParams } from 'react-router-dom';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
// import modelIcon from '../../../assets/images/modelIcon.svg';
import { Asterisk } from 'lucide-react';
import { Box } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import deepseekv32 from 'assets/images/deepseekv3.2.png';
import gptoss from 'assets/images/gptoss120b.png';
import kimik from 'assets/images/kimik2.png';
import llama33 from 'assets/images/llama3.3.png';
import llama4maverick from 'assets/images/llama4maverick.png';
import qwen32 from 'assets/images/qwen32b.png';
import glm45 from 'assets/images/glm4.5.png';







export const ModelSelector = () => {
  const [open, setOpen] = useState(false);
  const [showAllModels, setShowAllModels] = useState(false);
  // Use specific selectors to prevent unnecessary re-renders
  const selectedModel = useModelStore((state) => state.selectedModel);
  const { theme } = useTheme();


  // console.log('selectedModel', selectedModel)
  const models = useModelStore((state) => state.models);
  const openNewChatWithModel = useModelStore((state) => state.openNewChatWithModel);
  const userChangedModel = useModelStore((state) => state.userChangedModel);
  const setActiveConversationModelName = useChatStore((state) => state.setActiveConversationModelName);
  const { conversationId } = useParams();

  const shouldDisableDropdown = useChatStore((state) => {
    if (!conversationId) return false;
    const conv = state.conversations[conversationId];

    const streamingMessage = conv?.messages.find(
      (msg) => msg.recordId === conv?.streamingAssistantMessageId
    );
    return streamingMessage?.role === MessageRole.ASSISTANT || (conv?.activeStreamsMap?.size ?? 0) > 0 || conv?.assistantResponseRemaining !== 0;
  })

  const isStreaming = shouldDisableDropdown && !!conversationId;

  const currentPlan = useSubscriptionStore((state) => state.currentPlan);
  // const navigate = useNavigate();
  const { t } = useTranslation();
  useGetLlmModel();
  const isMobile = useIsMobile();

  // Memoize models to prevent unnecessary re-renders when array reference changes but content is same
  const modelsIds = useMemo(() => models.map(m => m.id).join(','), [models]);
  const memoizedModels = useMemo(() => models, [modelsIds, models.length]);

  // Show only first 5 models initially, or all if showAllModels is true
  const displayedModels = useMemo(() => {
    return showAllModels ? memoizedModels : memoizedModels.slice(0, 5);
  }, [memoizedModels, showAllModels]);

  const hasMoreModels = memoizedModels.length > 5;

  // Memoize the display name to prevent unnecessary re-renders
  const displayName = useMemo(() => {
    if (!selectedModel?.name) return '';
    if (isMobile && selectedModel.name.length > 12) {
      return selectedModel.name.slice(0, 12) + '...';
    }
    return selectedModel.name;
  }, [selectedModel?.name, isMobile]);

  // Memoize the handler to prevent dropdown re-renders
  const handleModelSelect = useCallback((modelId: string) => {
    if (selectedModel?.id !== modelId) {
      const model = memoizedModels.find((m) => m.id === modelId);
      if (model) {
        setActiveConversationModelName(model.id);
        openNewChatWithModel(model);
        userChangedModel(true);
        // navigate('/chat');
      }
    }
    setOpen(false);
  }, [memoizedModels, openNewChatWithModel, selectedModel?.id, setActiveConversationModelName, userChangedModel]);

  if (memoizedModels.length === 0 || !currentPlan || currentPlan.name === PLANS.NOPLAN) return null;

  const modelIconMap: Record<string, string> = {
    'DeepSeek-V3-0324': deepseekv32,
    'Qwen3-32B': qwen32,
    'Qwen2.5-VL-72B-Instruct': qwen32,
    'Llama-3.3-70B-Instruct-fast': llama33,
    'Llama-4-Maverick': llama4maverick,
    'gpt-oss-120b': gptoss,
    'Kimi-K2-Instruct': kimik,
    'GLM-4.5': glm45,
  };

  const modelsIconViewer = (model: any, selectedModel: any) => {
    const icon = modelIconMap[model.id];
    if (icon) {
      return <img className={`w-4 h-4`} src={icon} alt={model.name} />;
    }
    return (
      <Asterisk
        className={`w-5 h-5 ${selectedModel?.id === model.id ? 'text-primary' : ''
          }`}
      />
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isStreaming ? undefined : false}>
        <DropdownMenu
          open={isStreaming ? false : open}
          onOpenChange={(isOpen) => {
            if (isStreaming) return;
            setOpen(isOpen);
            // Reset to show only 5 models when dropdown closes
            if (!isOpen) {
              setShowAllModels(false);
            }
          }}

          modal={false}
        >
          <TooltipTrigger asChild>
            <div>
              <DropdownMenuTrigger asChild disabled={isStreaming}>

                <Button
                  variant="ghost"
                  disabled={isStreaming}
                  className={`rounded-2xl text-primary max-w-[120px] md:max-w-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} w-full justify-between transition-colors duration-200 h-[34px] focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none ${isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Box className={`sm:h-4 h-3 sm:w-4 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />

                  <span className={`text-sm  font-medium leading-5 overflow-hidden whitespace-nowrap ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`}>{displayName}</span>
                  <ChevronDown className={`sm:h-4 sm:w-4 h-3 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                </Button>

              </DropdownMenuTrigger>
            </div>
          </TooltipTrigger>

          <DropdownMenuContent
            align="start"
            className={`max-w-[200px] w-[200px] p-0 bg-white border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-lg rounded-xl`}
          >
            <div className='max-h-[324px] overflow-y-auto p-4'>
              {displayedModels.map((model, index) => (
                <DropdownMenuItem
                  key={model.id}
                  className="focus:bg-transparent focus:text-foreground p-0"
                  onSelect={(e) => {
                    e.preventDefault();
                    handleModelSelect(model.id);
                  }}
                >
                  <div className={`flex items-center justify-between w-full hover:cursor-pointer transition-all duration-200 ${index !== 0 ? "pt-4" : ""} ${index !== displayedModels.length - 1 || (index === displayedModels.length - 1 && hasMoreModels && !showAllModels) ? `border-b-[0.5px] ${theme === 'dark' ? 'border-border' : 'border-light-gray'} pb-4` : ''}`}

                  >
                    <div className="flex items-center gap-2 ">
                      <div className={`flex items-center justify-center ${selectedModel?.id === model.id ? 'text-primary' : ''}`}>
                        {/* <img src={modelIcon} alt={model.name} className={`w-4 h-4 ${selectedModel?.id === model.id ? 'text-primary' : ''}`} /> */}
                        {modelsIconViewer(model, selectedModel)}
                      </div>
                      <div>
                        <div className={`text-sm font-medium leading-5 ${selectedModel?.id === model.id ? 'text-primary' : ''}`}>{model.name}</div>
                        {/* <p className="w-56 text-xs text-gray-500">{t(model.description)}</p> */}
                      </div>
                    </div>

                    {/* {selectedModel?.id === model.id && (
                      <div className="flex items-center justify-center w-4 h-4 bg-primary rounded-full">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )} */}
                  </div>
                </DropdownMenuItem>
              ))}
              {hasMoreModels && !showAllModels && (

                <div

                  className="w-full text-sm font-medium pt-4 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAllModels(!showAllModels);
                  }}
                >
                  {/* {showAllModels ? t('VIEW_LESS') || 'View Less' : t('VIEW_MORE') || 'View More'}
                     */}
                  {t('VIEW_MORE') || 'View more...'}
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Tooltip content - only shows when streaming */}
        {isStreaming && <TooltipContent>
          <div className="text-xs py-1">
            {t('CANT_CHANGE_MODEL_WHILE_STREAMING')}
          </div>
        </TooltipContent>}
      </Tooltip>
    </TooltipProvider >
  );
};
